import React from "react";
import { useTranslation } from "react-i18next";
import { generatePath, Link, useLocation } from "react-router";

import { Claim } from "shared/api/claims/api";
import { useClaimsSchema } from "shared/schemas/claimsSchema";
import { EventTypeEnum } from "shared/types";

import { DETAILS_RELATED_EVENTS_ID } from "pages/Dashboard/components/constants";
import AiSummaryAndFields from "pages/Dashboard/components/EventDetails/AiSummaryAndFields";
import EventDetailsHeader from "pages/Dashboard/components/EventDetails/EventDetailsHeader";
import EventDetailsTabbed from "pages/Dashboard/components/EventDetails/EventDetailsTabbed";
import VINLink from "pages/Dashboard/components/VINLink";
import AttributesCard from "pages/shared/Dashboard/AttributesCard";
import HeaderFields from "pages/shared/Dashboard/HeaderFields";
import { LandingPageViewConfig } from "pages/shared/Dashboard/types";
import { getDefaultDetailsConfig } from "pages/shared/Dashboard/utils";
import { getAttributeValue } from "pages/shared/schemaDefinitions/utils";

import Typography from "features/components/Typography";
import AttachmentsCard from "features/ui/Attachments/AttachmentsCard";
import { Images } from "features/ui/Images/Images";

import { routes } from "services/routes";

import RelatedEvents from "./RelatedEvents";

interface Props {
  claim: Claim;
  onNext?: () => void;
  onPrevious?: () => void;
  config: LandingPageViewConfig;
  count?: number;
  claimIndex?: number;
  smartGroupId?: string;
  isEventDetailPage?: boolean;
}

const ClaimDetails = ({
  config,
  claim,
  onNext,
  onPrevious,
  count,
  claimIndex,
  smartGroupId,
  isEventDetailPage,
}: Props) => {
  const { t } = useTranslation();
  const location = useLocation();
  const { getDisplayLabel, getSchemaForAccessor } = useClaimsSchema([]);

  const detailsConfig =
    config.eventDetails ?? getDefaultDetailsConfig(config.eventType);
  const dateField = config.dateField;

  const smartGroupName =
    smartGroupId &&
    claim.smartGroups?.find((group) => group.ID === smartGroupId)?.name;

  const titleLinkPath = generatePath(routes.claimAnalyticsDetails, {
    id: claim.ID,
  });
  const title = getAttributeValue(
    detailsConfig.titleField,
    claim,
    getSchemaForAccessor,
    t
  );

  // Merge and deduplicate photos from both claim-specific and vehicle-level
  const mergedPhotos = React.useMemo(() => {
    const allPhotos = [...(claim.photos ?? []), ...(claim.vehiclePhotos ?? [])];

    // Deduplicate by ID
    const uniquePhotos = new Map();
    allPhotos.forEach((photo) => {
      if (!uniquePhotos.has(photo.ID)) {
        uniquePhotos.set(photo.ID, photo);
      }
    });

    return Array.from(uniquePhotos.values());
  }, [claim.photos, claim.vehiclePhotos]);

  // Merge and deduplicate attachments from both claim-specific and vehicle-level
  const mergedAttachments = React.useMemo(() => {
    const allAttachments = [
      ...(claim.attachments ?? []),
      ...(claim.vehicleAttachments ?? []),
    ];

    // Deduplicate by ID
    const uniqueAttachments = new Map();
    allAttachments.forEach((attachment) => {
      if (!uniqueAttachments.has(attachment.ID)) {
        uniqueAttachments.set(attachment.ID, attachment);
      }
    });

    return Array.from(uniqueAttachments.values());
  }, [claim.attachments, claim.vehicleAttachments]);

  const imageHeader = (
    <div className="flex flex-row gap-4 items-center">
      <div className="flex flex-col">
        <div className="font-bold text-xs">
          {getDisplayLabel(
            detailsConfig.titleField,
            t("schema:dashboard.claim_key")
          )}
        </div>
        <div className="text-xs">{title}</div>
      </div>
      <div className="flex flex-col">
        <div className="font-bold text-xs">
          {getDisplayLabel("VIN", t("common:vin"))}
        </div>
        <div className="underline text-xs">
          <VINLink config={config} vin={claim.VIN} />
        </div>
      </div>
      <div className="flex flex-col">
        <div className="font-bold text-xs">
          {t("schema:dashboard.claim_date")}
        </div>
        <div className="text-xs">
          {getAttributeValue(dateField, claim, getSchemaForAccessor, t)}
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex flex-col h-full text-xs">
      <EventDetailsHeader
        config={config}
        event={claim}
        dateField={dateField}
        onNext={onNext}
        onPrevious={onPrevious}
        currentIndex={claimIndex}
        count={count}
        getDisplayLabel={getDisplayLabel}
        getSchemaForAccessor={getSchemaForAccessor}
        smartGroupName={smartGroupName}
        isEventDetailPage={isEventDetailPage}
      />
      <div className="overflow-auto flex-1 bg-white">
        {/*main*/}
        <div className="bg-white">
          <div className="p-4 px-8 flex flex-col gap-4">
            <div className="border-b-2 border-gray-700 pb-2">
              <div className="font-bold pb-2">
                {getDisplayLabel("externalID", t("schema:dashboard.claim_key"))}
              </div>
              <div className="flex justify-between items-start -mt-2">
                <Typography variant="h2">
                  {location.pathname === titleLinkPath && title}
                  {location.pathname !== titleLinkPath && (
                    <Link to={titleLinkPath} className="hover:underline">
                      {title}
                    </Link>
                  )}
                </Typography>
                <HeaderFields
                  fields={detailsConfig.headerFields ?? []}
                  item={claim}
                  getDisplayLabel={getDisplayLabel}
                  getSchemaForAccessor={getSchemaForAccessor}
                />
              </div>
            </div>
            <div className="flex flex-row gap-12 h-fit">
              <div className="flex flex-col flex-1 gap-4">
                <Images
                  images={mergedPhotos}
                  title={claim.externalID}
                  imageHeader={imageHeader}
                />
                <AttachmentsCard
                  resourceType={EventTypeEnum.CLAIM}
                  itemID={claim.ID}
                  itemName={claim.externalID || ""}
                  attachments={mergedAttachments}
                />
              </div>

              <div className="flex-1 min-w-0">
                <AiSummaryAndFields
                  event={claim}
                  config={config}
                  getDisplayLabel={getDisplayLabel}
                  getSchemaForAccessor={getSchemaForAccessor}
                />
              </div>
            </div>
          </div>
        </div>

        {config.eventDetailsNew?.useNewConfig ? (
          <EventDetailsTabbed
            event={claim}
            config={config}
            getSchemaForAccessor={getSchemaForAccessor}
          />
        ) : (
          detailsConfig.cardRows.map((cardRow, index) => (
            <div className="flex flex-row gap-4 p-4 px-8 pb-0" key={index}>
              {cardRow.map((card, index) => (
                <div className={card.containerClass} key={index}>
                  <AttributesCard
                    item={claim}
                    attributes={card.fields}
                    title={card.title}
                    direction={card.type}
                    getSchemaForAccessor={getSchemaForAccessor}
                  />
                </div>
              ))}
            </div>
          ))
        )}

        <div className="mt-6" id={DETAILS_RELATED_EVENTS_ID}>
          {detailsConfig.relatedEvents.map((group, index) => (
            <div
              id={`${DETAILS_RELATED_EVENTS_ID}-${index}`}
              data-testid={`qv-details-related-event-group-${group.title}`}
              key={`${group.title}_${index}`}
            >
              <RelatedEvents
                config={group}
                event={claim}
                vin={claim.VIN}
                eventType={EventTypeEnum.CLAIM}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ClaimDetails;
